/**
 * Reasons a leasing operator can attach when sending a DigaVouch follow-up
 * request from the "Add Applicant" dialog. Used as the source of truth for:
 *   - The reason dropdown in AddApplicantDialog
 *   - The reason chip shown on the Applicant Overview page
 *   - The reason chip shown on the Case Review page
 *
 * `value` is what we persist + send to the backend in `reason`. `label` is
 * what the operator sees. `tooltip` appears on hover/focus on the dropdown
 * option and on the read-only chips.
 *
 * Backend-stable `value` keys are preserved so historical rows keep mapping
 * to the right label. Labels and the new `name_contact_mismatch` value are
 * documented for the backend team in
 * docs/backend-brief-follow-up-reasons.md.
 */

export interface FollowUpReason {
  value: string;
  label: string;
  tooltip: string;
}

export const FOLLOW_UP_REASONS: FollowUpReason[] = [
  {
    value: "thin_credit_file",
    label: "Limited identity history available",
    tooltip:
      "Use this when standard screening does not surface enough history to comfortably complete review.",
  },
  {
    value: "new_to_country",
    label: "Applicant has limited U.S. history",
    tooltip:
      "Use this when the applicant may not yet have a traditional U.S. identity or background footprint.",
  },
  {
    value: "additional_identity_confidence",
    label: "Identity signals are incomplete",
    tooltip:
      "Use this when the leasing team wants more corroboration of the person behind the application.",
  },
  {
    value: "application_clarification",
    label: "Application information needs confirmation",
    tooltip:
      "Use this when submitted details need additional support before review can continue.",
  },
  {
    value: "name_contact_mismatch",
    label: "Name or contact information mismatch",
    tooltip:
      "Use this when the name, email, or phone on the application doesn't match other records.",
  },
  {
    value: "manual_verification",
    label: "Property team requested manual review",
    tooltip:
      "Use this when the leasing team wants to send a DigaVouch request without selecting a specific trigger.",
  },
];

const BY_VALUE: Record<string, FollowUpReason> = Object.fromEntries(
  FOLLOW_UP_REASONS.map((r) => [r.value, r]),
);

const BY_LABEL: Record<string, FollowUpReason> = Object.fromEntries(
  FOLLOW_UP_REASONS.map((r) => [r.label.toLowerCase(), r]),
);

/**
 * Resolve a stored reason (either canonical `value` or legacy free-text
 * label) back to a FollowUpReason. Returns null when the stored string
 * doesn't match anything we know — callers should then render it raw.
 */
export function resolveFollowUpReason(stored?: string | null): FollowUpReason | null {
  if (!stored) return null;
  return (
    BY_VALUE[stored] ??
    BY_LABEL[stored.trim().toLowerCase()] ??
    null
  );
}
